import { useState } from 'react';
import { T, useGT } from 'gt-react';
import type { WorkbookResult, WorkbookSheet } from '@erp/shared-types';
import { resultColumns } from '@erp/workbooks';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { WorksheetColumnValidations } from '../WorksheetColumnValidations';
import {
  validationStates,
  type ChangeSheetResult,
} from '../worksheetValidations';

/**
 * A pivot's or freeform sheet's Validations, beside its editor: a Result column (its Row labels
 * and value columns, those its data spreads into included) and its rules,
 * checked over every row of the Result by each Run.
 */
export function PivotValidations({
  sheet,
  result,
  readOnly,
  change,
}: {
  sheet: WorkbookSheet;
  result?: WorkbookResult;
  readOnly: boolean;
  change: ChangeSheetResult;
}) {
  const gt = useGT();
  const columns = resultColumns(sheet, result?.columns);
  const [chosen, choose] = useState<string | null>(null);
  const column =
    columns.find((item) => item.name === chosen) ??
    columns.find((item) =>
      sheet.validations?.some((rule) => rule.column === item.name),
    ) ??
    columns[0];
  if (!column) return null;
  return (
    <section
      className="mt-3 flex flex-col gap-2 border-t pt-3"
      aria-label={gt('Worksheet validations', {
        context: 'worksheet validations panel',
      })}
    >
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        <T context="pivot validations column picker">Validate column</T>
        <Select value={column.name} onValueChange={choose}>
          <SelectTrigger className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {columns.map((item) => (
              <SelectItem key={item.name} value={item.name}>
                {item.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      <WorksheetColumnValidations
        sheet={sheet}
        column={column.name}
        type={column.type}
        states={validationStates(sheet, result)}
        readOnly={readOnly}
        change={change}
      />
    </section>
  );
}
